import { ImageIcon, Loader2, Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { TenantLogoWatermark } from '@/components/ui/tenant-logo-watermark'
import type { ProfileData } from '@/hooks/use-profile'
import { cn } from '@/lib/utils'

export interface ProfileBannerSectionProps {
  profile: ProfileData
  bannerUrl?: string | null
  isUploadingBanner?: boolean
  onEditClick: () => void
  /** Extra classes on outer section wrapper. */
  className?: string
}

/** View-only banner preview with pencil affordance — editing happens in Artifact Canvas. */
export function ProfileBannerSection({
  profile,
  bannerUrl,
  isUploadingBanner = false,
  onEditClick,
  className,
}: ProfileBannerSectionProps) {
  return (
    <section
      className={cn(
        'relative z-[1] overflow-visible mb-1.5',
        className
      )}
    >
      <div
        className="relative overflow-hidden rounded-xl border border-[var(--page-background)] bg-muted/40 group/banner"
        {...(profile.id ? { 'data-agent-highlight': `profile:${profile.id}:cover` } : {})}
      >
        <div
          className={cn(
            'relative flex h-60 w-full shrink-0 overflow-hidden',
            isUploadingBanner && 'pointer-events-none'
          )}
          aria-hidden={isUploadingBanner}
        >
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="relative isolate h-full w-full">
              <TenantLogoWatermark />
              <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
                <ImageIcon className="size-10 text-muted-foreground opacity-60" aria-hidden />
              </div>
            </div>
          )}
          {isUploadingBanner && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
              <Loader2 className="size-8 animate-spin text-foreground" aria-hidden />
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className="absolute right-2 top-2 z-[2] shrink-0 bg-background/80 backdrop-blur-sm opacity-100 transition-opacity hover:bg-accent md:right-4 md:top-4 md:opacity-0 md:group-hover/banner:opacity-100 focus-visible:opacity-100"
          onClick={onEditClick}
          aria-label="Edit cover image"
          title="Edit cover image"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    </section>
  )
}
